import { useState, useEffect, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { productLabel } from '../../utils/productCatalog';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { useLocale } from '../../utils/LocaleContext';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const S = {
  burgundy: '#800020',
  burgundy2: '#722f37',
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textMuted: '#64748b',
  borderLight: '#e2e8f0',
  bgCard: '#f8fafc',
  bgHover: '#f1f5f9',
};

const STATUS_COLOR = {
  active: { bg: '#dcfce7', color: '#15803d' },
  bound: { bg: '#dbeafe', color: '#1d4ed8' },
  pending: { bg: '#fef3c7', color: '#92400e' },
  expired: { bg: '#fee2e2', color: '#991b1b' },
  cancelled: { bg: '#f1f5f9', color: '#64748b' },
  lapsed: { bg: '#ede9fe', color: '#7c3aed' },
};

// US-only LOB family enums that have no counterpart in the shared
// product catalogue. Everything else — product codes and the four
// Swiss/health families — resolves through productLabel() so it honours
// the interface language. These stay here because they are only ever
// reached by US agency tenants and have no i18n keys yet.
const EXTRA_LOB_LABEL_KEYS = {
  errors_omissions: 'screens.policiesScreen.productErrorsOmissions',
  property:         'screens.policiesScreen.productProperty',
  workers_comp:     'screens.policiesScreen.productWorkersComp',
  auto:             'screens.policiesScreen.productAuto',
  health:           'screens.policiesScreen.productHealth',
  casualty:         'screens.policiesScreen.productCasualty',
  commercial:       'screens.policiesScreen.productCommercial',
};

const usePolicyDetailsFunctions = () => {
  const { t } = useTranslation();
  // The Claims tab must only appear when the tenant has Claims enabled.
  // Reading tenantConfig here keeps the tab list in sync with the sidebar
  // gating — a tenant that doesn't see a Claims sidebar item must not see
  // a Claims tab tucked inside another screen either.
  const features = useSelector(state => state?.tenantConfig?.config?.features) || {};
  const TABS = [
    { id: 'Overview', label: t('screens.policiesScreen.detail.tabOverview') },
    ...(features.claims
      ? [{ id: 'Claims', label: t('screens.policiesScreen.detail.tabClaims') }]
      : []),
    { id: 'Documents', label: t('screens.policiesScreen.detail.tabDocuments') },
    { id: 'Audit', label: t('screens.policiesScreen.detail.tabAudit') },
  ];
  const history = useHistory();
  const { id } = useParams();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('Overview');

  useLocale();

  const fetchPolicy = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${BASE_URL}/policies/${id}`, { withCredentials: true });
      const p = resp.data?.data;
      if (!p) throw new Error('Policy not found');
      setPolicy(p);
    } catch (e) {
      setError(e.response?.data?.message || e.message || t('screens.policiesScreen.detail.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPolicy();
  }, [fetchPolicy]);

  const status = policy?.status || 'active';
  const statusCfg = STATUS_COLOR[status] || STATUS_COLOR.active;
  const policyNumber = policy?.policyNumber || policy?._id;
  // Product code wins over the family enum — PGMA/PGM/IJM all share the
  // daily_sickness family, so the family alone renders a PGMA policy
  // under the IJM name. Routed through i18n: this used to be a hardcoded
  // English map that stayed English with the interface in French.
  const extraKey = EXTRA_LOB_LABEL_KEYS[policy?.lineOfBusiness];
  const lob = (policy?.product || !extraKey)
    ? productLabel(t, policy?.product, policy?.lineOfBusiness, 'long')
    : t(extraKey, { defaultValue: policy?.lineOfBusiness });
  const holderName = policy?.policyholder?.name || '—';
  const annualPremium = policy?.annualPremium || policy?.premium || 0;

  // D-21: no way to edit a policy from the detail view — Anjani flagged
  // "Edit option is absent". Add a PATCH helper the Header can wire
  // through for the most-commonly-edited fields (status, startDate,
  // endDate, annualPremium, notes). Refetches on success so downstream
  // components see fresh data.
  const patchPolicy = async (patch) => {
    if (!id || !patch || typeof patch !== 'object') return { ok: false };
    try {
      await axios.patch(`${BASE_URL}/policies/${id}`, patch, { withCredentials: true });
      await fetchPolicy();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.response?.data?.message || e.message };
    }
  };

  return {
    S,
    TABS,
    history,
    id,
    policy,
    setPolicy,
    loading,
    setLoading,
    error,
    setError,
    activeTab,
    setActiveTab,
    fetchPolicy,
    patchPolicy,
    status,
    statusCfg,
    policyNumber,
    lob,
    holderName,
    annualPremium,
  };
};

export default usePolicyDetailsFunctions;
